// Where the River gets its days from. E2: a dev-time fake day (Wizard loops); E5: the server.
import { clock, SLOW, STREAM_BPM } from "../stream";
import {
  dayToDate,
  hashString,
  mixSeed,
  type Bpm,
  type Lineup,
  type LineupEntry,
  type LoopData,
  type PeriodDef,
} from "@eb/shared";

export interface LineupSource {
  readonly name: string;
  epochMs(): number;
  /** A day's period of this site's stream, or of `bpm` (songs of the other stream on tapes). */
  period(day: number, bpm?: Bpm): Promise<PeriodDef>;
  lineup(day: number): Promise<Lineup>;
  /** Loop note data, batched. */
  loops(ids: string[]): Promise<Map<string, LoopData>>;
}

interface FakeFile {
  version: number;
  templates: { period: PeriodDef; loops: LoopData[] }[];
  authors: string[];
}

/** Maps any day onto one of the fake-day templates (deterministic, no server needed). */
export class FakeSource implements LineupSource {
  readonly name = "fake";
  private file: Promise<FakeFile> | null = null;
  constructor(private readonly epoch: number) {}

  epochMs(): number {
    return this.epoch;
  }

  private load(): Promise<FakeFile> {
    this.file ??= fetch("/dev/fake-day.json").then((r) => {
      if (!r.ok) throw new Error(`fake day: HTTP ${r.status} (run npm run gen-fake-day)`);
      return r.json() as Promise<FakeFile>;
    });
    return this.file;
  }

  private template(f: FakeFile, day: number) {
    return f.templates[((day % f.templates.length) + f.templates.length) % f.templates.length];
  }

  async period(day: number): Promise<PeriodDef> {
    const f = await this.load();
    return { ...this.template(f, day).period, day, ...(SLOW ? { bpm: 72 as const } : {}) };
  }

  async lineup(day: number): Promise<Lineup> {
    const f = await this.load();
    const t = this.template(f, day);
    const entries: LineupEntry[] = [];
    for (let slot = 0; slot < clock.SLOTS_PER_DAY; slot++) {
      const h = mixSeed(hashString(`day${day}`), slot);
      const idx = h % t.loops.length;
      const human = h % 5 !== 0;
      entries.push({
        slot,
        loopId: `fake:${((day % f.templates.length) + f.templates.length) % f.templates.length}:${idx}`,
        author: human ? f.authors[(h >>> 8) % f.authors.length] : "Wizard",
        title: null,
        kind: human ? "human" : "filler",
        wizardShare: human ? ((h >>> 16) % 3 === 0 ? ((h >>> 20) % 60) / 100 : 0) : 1,
        gain: 1,
      });
    }
    return { bpm: STREAM_BPM, day, date: dayToDate(day, this.epoch), frozenAt: null, entries, version: 1 };
  }

  async loops(ids: string[]): Promise<Map<string, LoopData>> {
    const f = await this.load();
    const out = new Map<string, LoopData>();
    for (const id of ids) {
      const m = /^fake:(\d+):(\d+)$/.exec(id);
      if (m) out.set(id, f.templates[Number(m[1])].loops[Number(m[2])]);
    }
    return out;
  }
}
